/**
 * What a step's mapping or a gate's conditions may read (Feature 71, 1.6.3;
 * Feature 72 D5, D10), as the picker offers it: the first task's input,
 * `first`'s result, and the result and input of every step upstream of where
 * the reference is used, each walked into its nested fields and list items
 * from the capabilities' schemas. A gate's picker puts the node before it
 * first, then the earlier nodes, each under its group's heading.
 *
 * Each option also describes its field in short form (Feature 73 D2): its
 * own name, where it sits within its node's input or result, its type, and
 * the text the closed field shows. A list's items are walked as `[]`, never
 * a position (Feature 82 D1, D2): such an option's value is the picker's
 * own, `….result.[].is_focus`, which a pick turns into a list test or a
 * list filter (D9, D11); it marks the list it is in, and a place that
 * takes one value, or a list within an item, shows it disabled (D11, D12).
 * A stored reference with a position (`.0`) still reads as `[0]`.
 *
 * On a call (Feature 78 D3, D11) `first` is the request: its input is
 * offered, and its result, which does not exist when its steps start, is
 * listed once and disabled, saying why, so nobody hunts for it.
 */
import type { CapabilitySchema, FieldSpec } from '@optum/api-client/types';

import type { ComboboxOption } from '../../ui/Combobox.js';

import { FIRST, upstream, type Draft } from './draft.js';
import { isCall } from './start.js';

/** Input the platform fills on every task; a template may not map it (the agent's own list). */
export const PLATFORM_FIELDS: ReadonlySet<string> = new Set([
  'agent_id',
  'capability_id',
  'user_idp',
  'user_sub',
  'acl_group_ids',
  'retrieval_token',
  'retrieval_url',
  'is_enable_streaming',
]);

// How far the picker walks into nested objects and list items.
const MAX_DEPTH = 4;
/** The picker's list opens this wide, past the inspector's field (Feature 73 D2). */
export const PICKER_WIDTH = 380;
// A path segment that indexes a list.
const INDEX = /^\d+$/;
/** The picker's segment for a list's items: any item, no position (Feature 82 D1). */
export const ITEMS = '[]';
const WHOLE_RESULT = 'the whole result';
/** Said once, under the first item field a condition offers (Feature 82 D7). */
export const ITEMS_NOTE_TEST = '[] is any item: picking it builds a list test';
/** Said once, under the first item field a list input offers. */
export const ITEMS_NOTE_FILTER = '[] is any item: picking it builds a list filter';
/** Why a place that takes one value does not offer a list's item field (D11). */
export const ONE_VALUE_WORDS = 'a list: this takes one value';
/** Why a field inside a list within a list item is not offered (D12). */
export const NESTED_WORDS = 'a list inside a list item: test it as a whole';

/** A field's type as the picker knows it; the whole result is an object. */
export type ReferenceType = FieldSpec['type'];

/** Where a picked reference goes: a condition, a list input's mapping, or one value. */
export type Place = 'condition' | 'list' | 'one';

/** A field inside a list: the list's stored path, the field as its item reads it, and depth. */
export interface Within {
  /** The list's own path, `steps.first.result.result`. */
  list: string;
  /** The field read from one item, `item.is_focus`. */
  item: string;
  /** Inside a list within an item: one level only (D12). */
  nested: boolean;
}

export interface ReferenceOption {
  /** The path the template stores. */
  value: string;
  /** The path and its type in one line, `steps.first.result.success · boolean`. */
  label: string;
  /** The field's own name, `passed`; `the whole result` for a node's result. */
  name: string;
  /** Where the field sits within its node's input or result, `result[0].budget`. */
  where: string;
  type: ReferenceType;
  /** The node it belongs to, `first` or a step's name. */
  node: string;
  side: 'input' | 'result';
  /** The closed field's text, `first › result[0].budget.passed`. */
  short: string;
  /** The heading it sits under, `first · result`. */
  group: string;
  /** The first-item note, on the first field inside a list (D15). */
  note?: string | undefined;
  /** Listed but not offered: the call's result, which has not happened yet (Feature 78 D3). */
  disabled?: boolean | undefined;
  /** On a field inside a list: the list it is in, and the field as an item reads it. */
  within?: Within | undefined;
}

/** Where a reference is used: in a step's mapping, or in the rules of a link from a node. */
export type ReferenceTarget = { kind: 'step'; name: string } | { kind: 'link'; from: string };

export type Schemas = ReadonlyMap<string, CapabilitySchema>;

/** A path within a side, as read: a list's items or index joined to it, `result[].budget`. */
export function readablePath(segments: readonly string[]): string[] {
  const parts: string[] = [];
  for (const segment of segments) {
    const marker = segment === ITEMS ? ITEMS : INDEX.test(segment) ? `[${segment}]` : null;
    if (marker !== null && parts.length > 0) parts[parts.length - 1] += marker;
    else parts.push(segment);
  }
  return parts;
}

/** Where a picker-only `[]` path's list ends: the list's path and the rest after `.[]`. */
function splitItems(path: string): { list: string; rest: string } | null {
  const at = path.indexOf(`.${ITEMS}`);
  return at < 0 ? null : { list: path.slice(0, at), rest: path.slice(at + ITEMS.length + 1) };
}

/** What a picker-only path says about its list: none outside a list (D9, D12). */
export function withinOf(path: string): Within | null {
  const split = splitItems(path);
  if (split === null) return null;
  return {
    list: split.list,
    item: `item${split.rest}`,
    nested: split.rest.includes(`.${ITEMS}`),
  };
}

/** Where a node's side starts in a path: `input` for first's input, else `steps.<node>.<side>`. */
function sidePrefix(node: string, side: 'input' | 'result'): string {
  return node === FIRST && side === 'input' ? 'input' : `steps.${node}.${side}`;
}

/** One option, described from its path within its node's side. */
function describe(
  node: string,
  side: 'input' | 'result',
  path: string,
  type: ReferenceType,
): ReferenceOption {
  const prefix = sidePrefix(node, side);
  const segments = path === prefix ? [] : path.slice(prefix.length + 1).split('.');
  const parts = readablePath(segments);
  const name = parts.at(-1) ?? WHOLE_RESULT;
  const within = parts.join('.');
  const shown = side === 'input' ? `input.${within}` : within || WHOLE_RESULT;
  const list = withinOf(path);
  return {
    ...(list ? { within: list } : {}),
    value: path,
    label: `${path} · ${path === prefix ? WHOLE_RESULT : type}`,
    name,
    where: parts.slice(0, -1).join('.'),
    type,
    node,
    side,
    short: `${node} › ${shown}`,
    group: `${node} · ${side}`,
  };
}

function fieldPaths(
  node: string,
  side: 'input' | 'result',
  prefix: string,
  fields: readonly FieldSpec[],
  depth: number,
): ReferenceOption[] {
  const found: ReferenceOption[] = [];
  for (const field of fields) {
    const path = `${prefix}.${field.name}`;
    found.push(describe(node, side, path, field.type));
    if (depth >= MAX_DEPTH) continue;
    if (field.children) found.push(...fieldPaths(node, side, path, field.children, depth + 1));
    if (field.item) {
      const items = `${path}.${ITEMS}`;
      // An item with fields is reached through them; a plain item is offered itself.
      if (field.item.children) {
        found.push(...fieldPaths(node, side, items, field.item.children, depth + 1));
      } else {
        found.push(describe(node, side, items, field.item.type));
      }
    }
  }
  return found;
}

function resultPaths(name: string, schema: CapabilitySchema | undefined): ReferenceOption[] {
  const whole = sidePrefix(name, 'result');
  return [
    describe(name, 'result', whole, 'object'),
    ...fieldPaths(name, 'result', whole, schema?.output_fields ?? [], 1),
  ];
}

function inputPaths(name: string, schema: CapabilitySchema | undefined): ReferenceOption[] {
  return fieldPaths(name, 'input', sidePrefix(name, 'input'), mappableFields(schema), 1);
}

/** The call's result, listed once and disabled: its steps start before it exists. */
function callResult(): ReferenceOption {
  return {
    ...describe(FIRST, 'result', sidePrefix(FIRST, 'result'), 'object'),
    name: 'the call’s result',
    note: 'none yet: its steps start as it is accepted',
    disabled: true,
  };
}

/** A node's own readable paths: `first`'s input and result, or a step's input and result. */
function nodePaths(draft: Draft, name: string, schemas: Schemas): ReferenceOption[] {
  if (name === FIRST && isCall(draft.start)) {
    return [...inputPaths(FIRST, schemas.get(draft.firstCapabilityId)), callResult()];
  }
  if (name === FIRST) {
    // On a schedule too, `first` is its capability: its input is the fixed
    // input, its result the task's (Feature 80 D13).
    const first = schemas.get(draft.firstCapabilityId);
    return [...inputPaths(FIRST, first), ...resultPaths(FIRST, first)];
  }
  const schema = schemas.get(draft.steps.find((s) => s.name === name)?.capability_id ?? '');
  return [...inputPaths(name, schema), ...resultPaths(name, schema)];
}

/**
 * A stored reference in the picker's short form, `first › result[0].budget`,
 * whatever the schemas hold; `steps.first.input` reads as first's input.
 */
export function shortRef(path: string): string {
  const parts = path.split('.');
  if (parts[0] === 'input') {
    return parts.length === 1 ? `${FIRST} › input` : describe(FIRST, 'input', path, 'any').short;
  }
  const [head, node, side] = parts;
  if (head !== 'steps' || !node || (side !== 'input' && side !== 'result')) return path;
  if (side === 'input' && parts.length === 3) return `${node} › input`;
  if (node === FIRST && side === 'input') {
    return describe(FIRST, 'input', ['input', ...parts.slice(3)].join('.'), 'any').short;
  }
  return describe(node, side, path, 'any').short;
}

/**
 * Why the picker does not offer a stored reference: the call has no result
 * yet (Feature 78 D18), or the reference is not upstream of where it is used.
 */
export function notOfferedWords(references: readonly ReferenceOption[], ref: string): string {
  const call = references.find((r) => r.disabled);
  return call && (ref === call.value || ref.startsWith(`${call.value}.`))
    ? 'the call has no result yet'
    : 'not upstream';
}

/** The first reference offered, or first's input when there is none. */
export function firstReference(references: readonly ReferenceOption[]): string {
  return references.find((r) => !r.disabled)?.value ?? 'input';
}

/** The options a picker draws: the name first, where it sits, its type, under its group. */
export function pickerOptions(references: readonly ReferenceOption[]): ComboboxOption[] {
  return references.map((r) => ({
    value: r.value,
    label: r.name,
    detail: r.where ? `in ${r.where}` : undefined,
    aside: r.type,
    meta: r.note,
    group: r.group,
    selectedText: r.short,
    title: r.value,
    disabled: r.disabled,
  }));
}

/** The input fields a step may map: its capability's, without the platform's. */
export function mappableFields(schema: CapabilitySchema | undefined): FieldSpec[] {
  return (schema?.input_fields ?? []).filter((f) => !PLATFORM_FIELDS.has(f.name));
}

/** Why a field inside a list is not offered at a place, or null when it is (D11, D12). */
function notHere(within: Within, place: Place): string | null {
  if (within.nested) return NESTED_WORDS;
  return place === 'one' ? ONE_VALUE_WORDS : null;
}

/**
 * The options as a place offers them: a field inside a list disabled with
 * why where it cannot go, and the first offered one carrying the note on
 * what a pick builds (Feature 82 D7, D11, D12).
 */
export function forPlace(options: readonly ReferenceOption[], place: Place): ReferenceOption[] {
  const placed = options.map((o) => {
    const why = o.within ? notHere(o.within, place) : null;
    return why === null ? o : { ...o, disabled: true, note: why };
  });
  const first = placed.findIndex((o) => o.within && !o.disabled);
  const note = place === 'list' ? ITEMS_NOTE_FILTER : ITEMS_NOTE_TEST;
  return placed.map((o, i) => (i === first ? { ...o, note } : o));
}

/** Every reference readable where `target` is, in the order a reader meets them. */
export function referencesFor(
  draft: Draft,
  target: ReferenceTarget,
  schemas: Schemas,
): ReferenceOption[] {
  return gathered(draft, target, schemas);
}

function gathered(draft: Draft, target: ReferenceTarget, schemas: Schemas): ReferenceOption[] {
  if (target.kind === 'link') {
    // A gate reads the node before it first, then anything upstream of that node.
    const earlier = [FIRST, ...draft.steps.map((s) => s.name)].filter(
      (name) => name !== target.from && upstream(draft, target.from).has(name),
    );
    return [
      ...nodePaths(draft, target.from, schemas).map((o) => ({
        ...o,
        group: `${o.group} · the node before`,
      })),
      ...earlier.flatMap((name) => nodePaths(draft, name, schemas)),
    ];
  }
  const readable = upstream(draft, target.name);
  return [FIRST, ...draft.steps.map((s) => s.name)]
    .filter((name) => name === FIRST || readable.has(name))
    .flatMap((name) => nodePaths(draft, name, schemas));
}
